import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Banner,
  Card,
  MarkdownBody,
  SectionTitle,
  SlaBadge,
  Stat,
  ageLabel,
  formatBytes,
} from "../components/ui";
import { ApiError, type Api } from "../lib/api";
import { MentionTextarea } from "../components/MentionTextarea";
import {
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  type BuildCandidate,
  type CannedResponse,
  type SavedView,
  type Ticket,
  type TicketMetrics,
  type TicketPriority,
  type TicketStatus,
} from "../lib/types";

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  pending: "Pending",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

const SEVERITY_LABEL: Record<TicketPriority, string> = {
  urgent: "Urgent",
  high: "High",
  normal: "Normal",
  low: "Low",
};

const SEVERITY_RANK: Record<TicketPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
const OPEN_STATUSES: TicketStatus[] = ["open", "in_progress", "pending"];

type SlaFilter = "all" | "at_risk" | "breached";
type Mode = "queue" | "metrics" | "settings";

/** Queue keys shown in the settings editor: a "default" fallback plus each category. */
const QUEUE_KEYS = ["default", "technical", "bug", "build", "account", "other"] as const;

const QUEUE_LABEL: Record<string, string> = {
  default: "Default (fallback)",
  technical: "Technical",
  bug: "Bug",
  build: "Build",
  account: "Account",
  other: "Other",
};

function isOpen(t: Ticket): boolean {
  return OPEN_STATUSES.includes(t.status);
}

function severityBadge(p: TicketPriority): string {
  return p === "urgent" || p === "high" ? "badge-accent" : "badge";
}

function statusBadge(s: TicketStatus): string {
  return s === "closed" || s === "resolved" ? "badge" : "badge-accent";
}

export function matchesLocalTicketQuery(
  t: Pick<Ticket, "number" | "subject" | "accountName" | "requesterEmail">,
  query: string,
): boolean {
  const localQuery = query.trim().toLowerCase();
  if (localQuery === "") return true;
  const caseNumber = localQuery.match(/^(?:case\s+)?#?(\d+)$/);
  return (
    t.subject.toLowerCase().includes(localQuery) ||
    t.accountName.toLowerCase().includes(localQuery) ||
    t.requesterEmail.toLowerCase().includes(localQuery) ||
    (t.number !== undefined && caseNumber !== null && Number(caseNumber[1]) === t.number)
  );
}

function textOrDash(n: number | null): string {
  if (n == null) return "—";
  return `${Math.round(n)}`;
}

function topEntries(rec: Record<string, number>, limit = 10): Array<[string, number]> {
  return Object.entries(rec)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

function ascendingEntries(rec: Record<string, number>): Array<[string, number]> {
  return Object.entries(rec).sort((a, b) => a[0].localeCompare(b[0]));
}

export function Support({ api, meEmail }: { api: Api; meEmail: string }) {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from a staff notification email: /app/support?ticket=<id> opens that case.
  // An explicit list selection takes precedence; otherwise fall back to the URL param. §F-1
  const deepLinkId = searchParams.get("ticket");
  const activeTicketId = selectedId ?? deepLinkId;

  const [fStatus, setFStatus] = useState<"all" | TicketStatus>("all");
  const [fSeverity, setFSeverity] = useState<"all" | TicketPriority>("all");
  const [fAssignee, setFAssignee] = useState<"all" | "unassigned" | "me">("all");
  const [fSla, setFSla] = useState<SlaFilter>("all");

  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState<"local" | "server">("local");
  const [searchBusy, setSearchBusy] = useState(false);

  const [bulkSelectedIds, setBulkSelectedIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<"" | TicketStatus>("");
  const [bulkPriority, setBulkPriority] = useState<"" | TicketPriority>("");
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const [mode, setMode] = useState<Mode>("queue");
  const [metrics, setMetrics] = useState<TicketMetrics | null>(null);
  const [metricsFrom, setMetricsFrom] = useState("");
  const [metricsTo, setMetricsTo] = useState("");
  const [metricsBusy, setMetricsBusy] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  const [views, setViews] = useState<SavedView[]>([]);
  const [viewName, setViewName] = useState("");
  const [viewsError, setViewsError] = useState<string | null>(null);

  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState<{ escalated: number; autoClosed: number } | null>(null);

  async function load() {
    setError(null);
    try {
      const r = await api.adminListTickets();
      setTickets(r.tickets);
      setBulkSelectedIds([]);
    } catch {
      setError("Could not load tickets.");
    }
  }

  async function runServerSearch() {
    const q = query.trim();
    if (q === "") {
      await load();
      return;
    }
    setSearchBusy(true);
    setError(null);
    try {
      const r = await api.adminSearchTickets(q);
      setTickets(r.tickets);
      setBulkSelectedIds([]);
    } catch {
      setError("Could not run search.");
    } finally {
      setSearchBusy(false);
    }
  }

  async function loadViews() {
    setViewsError(null);
    try {
      const r = await api.listSavedViews();
      setViews(r.views);
    } catch {
      setViewsError("Could not load saved views.");
    }
  }

  async function saveCurrentView() {
    if (viewName.trim() === "") return;
    setViewsError(null);
    try {
      await api.saveSavedView({
        name: viewName.trim(),
        filter: {
          status: fStatus,
          severity: fSeverity,
          assignee: fAssignee,
          sla: fSla,
          query,
          searchMode,
        },
      });
      setViewName("");
      await loadViews();
    } catch {
      setViewsError("Could not save this view.");
    }
  }

  function applyView(view: SavedView) {
    const f = view.filter;
    setFStatus((f.status as "all" | TicketStatus) ?? "all");
    setFSeverity((f.severity as "all" | TicketPriority) ?? "all");
    setFAssignee((f.assignee as "all" | "unassigned" | "me") ?? "all");
    setFSla((f.sla as SlaFilter) ?? "all");
    setQuery((f.query as string) ?? "");
    setSearchMode((f.searchMode as "local" | "server") ?? "local");
  }

  async function deleteView(id: string) {
    setViewsError(null);
    try {
      await api.deleteSavedView(id);
      await loadViews();
    } catch {
      setViewsError("Could not delete this view.");
    }
  }

  async function runSweep() {
    setSweepBusy(true);
    setError(null);
    try {
      const r = await api.runSlaSweep();
      setSweepResult(r);
      await load();
    } catch {
      setError("Could not run SLA sweep.");
    } finally {
      setSweepBusy(false);
    }
  }

  async function runBulk() {
    if (bulkSelectedIds.length === 0) return;
    const patch: { status?: string; priority?: string; assigneeEmail?: string | null } = {};
    if (bulkStatus !== "") patch.status = bulkStatus;
    if (bulkPriority !== "") patch.priority = bulkPriority;
    if (bulkAssignee.trim() !== "") patch.assigneeEmail = bulkAssignee.trim();
    if (Object.keys(patch).length === 0) return;

    setBulkBusy(true);
    setError(null);
    try {
      await api.adminBulkTickets(bulkSelectedIds, patch);
      setBulkSelectedIds([]);
      setBulkStatus("");
      setBulkPriority("");
      setBulkAssignee("");
      await load();
    } catch {
      setError("Could not apply bulk changes.");
    } finally {
      setBulkBusy(false);
    }
  }

  async function loadMetrics() {
    setMetricsBusy(true);
    setMetricsError(null);
    try {
      const r = await api.adminTicketMetrics(metricsFrom || undefined, metricsTo || undefined);
      setMetrics(r.metrics);
    } catch {
      setMetricsError("Could not load metrics.");
    } finally {
      setMetricsBusy(false);
    }
  }

  useEffect(() => {
    void load();
    void loadViews();
  }, []);

  useEffect(() => {
    if (mode === "metrics" && !metrics && !metricsBusy) {
      void loadMetrics();
    }
  }, [mode]);

  const selected = useMemo(
    () => tickets?.find((t) => t.id === activeTicketId) ?? null,
    [tickets, activeTicketId],
  );

  function clearSelection() {
    setSelectedId(null);
    // Drop the deep-link param so returning to the queue doesn't immediately reopen it.
    if (deepLinkId) {
      const next = new URLSearchParams(searchParams);
      next.delete("ticket");
      setSearchParams(next, { replace: true });
    }
  }

  function upsert(ticket: Ticket) {
    setTickets((cur) => cur?.map((t) => (t.id === ticket.id ? ticket : t)) ?? [ticket]);
  }

  if (selected) {
    return (
      <SupportDetail
        api={api}
        meEmail={meEmail}
        ticket={selected}
        onChange={upsert}
        onBack={clearSelection}
        onReload={() => void load()}
      />
    );
  }

  const all = tickets ?? [];
  const open = all.filter(isOpen);

  const byStatus = (s: TicketStatus) => all.filter((t) => t.status === s).length;
  const bySeverity = (p: TicketPriority) => open.filter((t) => t.priority === p).length;
  const unassigned = open.filter((t) => !t.assigneeEmail).length;

  const topOpen = [...open]
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.priority] - SEVERITY_RANK[b.priority] ||
        a.createdAt.localeCompare(b.createdAt),
    )
    .slice(0, 5);

  const filtered = all
    .filter((t) => (fStatus === "all" ? true : t.status === fStatus))
    .filter((t) => (fSeverity === "all" ? true : t.priority === fSeverity))
    .filter((t) =>
      fAssignee === "all"
        ? true
        : fAssignee === "unassigned"
          ? !t.assigneeEmail
          : t.assigneeEmail?.toLowerCase() === meEmail.toLowerCase(),
    )
    .filter((t) =>
      fSla === "all" ? true : fSla === "at_risk" ? t.slaState === "at_risk" : t.slaState === "breached",
    )
    .filter((t) => {
      if (searchMode === "server") return true;
      return matchesLocalTicketQuery(t, query);
    })
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.priority] - SEVERITY_RANK[b.priority] ||
        b.updatedAt.localeCompare(a.updatedAt),
    );

  const allFilteredIds = filtered.map((t) => t.id);
  const allSelected =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => bulkSelectedIds.includes(id));

  return (
    <div>
      <SectionTitle
        kicker="Admin · Govern"
        title="Support console"
        desc="Triage and resolve customer cases. Full case content stays in PumaMesh storage while the support queue remains searchable, filterable, and SLA-driven."
      />

      {error && (
        <div className="mb-6">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      {sweepResult && (
        <div className="mb-6">
          <Banner tone="success">
            SLA sweep complete · escalated {sweepResult.escalated} · auto-closed {sweepResult.autoClosed}
          </Banner>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button
            className={mode === "queue" ? "btn-primary" : "btn-ghost"}
            onClick={() => setMode("queue")}
          >
            Queue
          </button>
          <button
            className={mode === "metrics" ? "btn-primary" : "btn-ghost"}
            onClick={() => setMode("metrics")}
          >
            Metrics
          </button>
          <button
            className={mode === "settings" ? "btn-primary" : "btn-ghost"}
            onClick={() => setMode("settings")}
          >
            Settings
          </button>
        </div>
        <button className="btn-ghost" disabled={sweepBusy} onClick={() => void runSweep()}>
          {sweepBusy ? "Running…" : "Run SLA sweep"}
        </button>
      </div>

      {mode === "settings" ? (
        <QueueSettingsPanel api={api} />
      ) : mode === "metrics" ? (
        <MetricsPanel
          metrics={metrics}
          loading={metricsBusy}
          error={metricsError}
          from={metricsFrom}
          to={metricsTo}
          onFromChange={setMetricsFrom}
          onToChange={setMetricsTo}
          onReload={() => void loadMetrics()}
        />
      ) : tickets === null ? (
        <Card>
          {error ? (
            <div className="space-y-3">
              <p className="text-sm text-ink-muted">
                The case queue could not be loaded. This does not mean there are no cases.
              </p>
              <button className="btn-primary" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : (
            <p className="text-sm text-ink-muted">Loading cases…</p>
          )}
        </Card>
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Open" value={byStatus("open") + byStatus("in_progress") + byStatus("pending")} />
            <Stat label="Unassigned (open)" value={unassigned} tone={unassigned > 0 ? "warn" : "ok"} />
            <Stat label="Resolved" value={byStatus("resolved")} />
            <Stat label="Closed" value={byStatus("closed")} />
          </div>

          <div className="mb-6 grid gap-6 lg:grid-cols-2">
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-ink">Open cases by severity</h3>
              <div className="grid grid-cols-4 gap-3">
                {TICKET_PRIORITIES.map((p) => (
                  <button
                    key={p}
                    className="rounded-sm border border-line px-3 py-3 text-left transition hover:border-accent/50"
                    onClick={() => {
                      setFSeverity(p);
                      setFStatus("all");
                      setFAssignee("all");
                    }}
                  >
                    <div className="text-2xl font-semibold text-ink">{bySeverity(p)}</div>
                    <div className="mt-1">
                      <span className={severityBadge(p)}>{SEVERITY_LABEL[p]}</span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            <Card className="min-w-0">
              <h3 className="mb-3 text-sm font-semibold text-ink">Top open cases</h3>
              {topOpen.length === 0 ? (
                <p className="text-sm text-ink-muted">No open cases.</p>
              ) : (
                <div className="grid gap-2">
                  {topOpen.map((t) => (
                    <button
                      key={t.id}
                      className="flex min-w-0 items-center justify-between gap-3 rounded-sm border border-line px-3 py-2 text-left transition hover:border-accent/50"
                      onClick={() => setSelectedId(t.id)}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className={`${severityBadge(t.priority)} whitespace-nowrap shrink-0`}>{SEVERITY_LABEL[t.priority]}</span>
                        <span className="whitespace-nowrap shrink-0"><SlaBadge state={t.slaState} /></span>
                        <span className="badge whitespace-nowrap shrink-0">{ageLabel(t.createdAt)} old</span>
                        {t.number !== undefined && <span className="badge whitespace-nowrap shrink-0">Case #{t.number}</span>}
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{t.subject}</span>
                      </span>
                      <span className="shrink-0 text-xs text-ink-dim">
                        {t.assigneeEmail ? "assigned" : "unassigned"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <Card className="mb-6">
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <div>
                <label className="label">Saved views</label>
                <select
                  className="input !w-64"
                  value=""
                  onChange={(e) => {
                    const v = views.find((x) => x.id === e.target.value);
                    if (v) applyView(v);
                  }}
                >
                  <option value="">Apply a saved view…</option>
                  {views.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[14rem] flex-1">
                <label className="label">Save current filters as</label>
                <input
                  className="input"
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  placeholder="Critical queue"
                />
              </div>
              <button className="btn-primary" onClick={() => void saveCurrentView()}>
                Save view
              </button>
              <button className="btn-ghost" onClick={() => void loadViews()}>
                Reload views
              </button>
            </div>
            {viewsError && <p className="text-xs text-danger">{viewsError}</p>}
            {views.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {views.map((v) => (
                  <span key={v.id} className="inline-flex items-center gap-1 rounded-sm border border-line px-2 py-1 text-xs text-ink">
                    {v.name}
                    <button className="text-ink-dim hover:text-danger" onClick={() => void deleteView(v.id)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div>
                <label className="label">Status</label>
                <select
                  className="input !w-40"
                  value={fStatus}
                  onChange={(e) => setFStatus(e.target.value as "all" | TicketStatus)}
                >
                  <option value="all">All</option>
                  {TICKET_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Severity</label>
                <select
                  className="input !w-40"
                  value={fSeverity}
                  onChange={(e) => setFSeverity(e.target.value as "all" | TicketPriority)}
                >
                  <option value="all">All</option>
                  {TICKET_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {SEVERITY_LABEL[p]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">SLA</label>
                <select className="input !w-40" value={fSla} onChange={(e) => setFSla(e.target.value as SlaFilter)}>
                  <option value="all">All</option>
                  <option value="at_risk">At risk</option>
                  <option value="breached">Breached</option>
                </select>
              </div>
              <div>
                <label className="label">Assignee</label>
                <select
                  className="input !w-44"
                  value={fAssignee}
                  onChange={(e) => setFAssignee(e.target.value as "all" | "unassigned" | "me")}
                >
                  <option value="all">Anyone</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="me">Assigned to me</option>
                </select>
              </div>
              <div>
                <label className="label">Search mode</label>
                <select
                  className="input !w-40"
                  value={searchMode}
                  onChange={(e) => setSearchMode(e.target.value as "local" | "server")}
                >
                  <option value="local">Local</option>
                  <option value="server">Server full-text</option>
                </select>
              </div>
              <div className="min-w-[12rem] flex-1">
                <label className="label">Search</label>
                <input
                  className="input"
                  placeholder={searchMode === "server" ? "Subject, body, comments…" : "Subject, account, requester…"}
                  value={query}
                  onChange={(e) => {
                    const next = e.target.value;
                    setQuery(next);
                    if (searchMode === "server" && next.trim() === "") void load();
                  }}
                />
              </div>
              {searchMode === "server" && (
                <button className="btn-primary" disabled={searchBusy} onClick={() => void runServerSearch()}>
                  {searchBusy ? "Searching…" : "Search"}
                </button>
              )}
              <button
                className="btn-ghost"
                onClick={() => {
                  setQuery("");
                  void load();
                }}
              >
                Clear
              </button>
              <button className="btn-ghost" onClick={() => void load()}>
                Refresh
              </button>
            </div>

            {bulkSelectedIds.length > 0 && (
              <div className="mb-4 rounded-sm border border-accent/40 bg-bg-soft p-3">
                <div className="mb-2 text-sm text-ink">{bulkSelectedIds.length} selected</div>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="label">Status</label>
                    <select
                      className="input !w-40"
                      value={bulkStatus}
                      onChange={(e) => setBulkStatus(e.target.value as "" | TicketStatus)}
                    >
                      <option value="">No change</option>
                      {TICKET_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Severity</label>
                    <select
                      className="input !w-40"
                      value={bulkPriority}
                      onChange={(e) => setBulkPriority(e.target.value as "" | TicketPriority)}
                    >
                      <option value="">No change</option>
                      {TICKET_PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {SEVERITY_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[14rem]">
                    <label className="label">Assignee</label>
                    <input
                      className="input"
                      placeholder="staff@pumamesh.com"
                      value={bulkAssignee}
                      onChange={(e) => setBulkAssignee(e.target.value)}
                    />
                  </div>
                  <button className="btn-primary" disabled={bulkBusy} onClick={() => void runBulk()}>
                    {bulkBusy ? "Applying…" : "Apply bulk action"}
                  </button>
                </div>
              </div>
            )}

            {tickets && filtered.length === 0 && (
              <p className="text-sm text-ink-muted">No cases match these filters.</p>
            )}

            <div className="mb-2 flex items-center gap-2 text-xs text-ink-dim">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setBulkSelectedIds(Array.from(new Set([...bulkSelectedIds, ...allFilteredIds])));
                  } else {
                    setBulkSelectedIds(bulkSelectedIds.filter((id) => !allFilteredIds.includes(id)));
                  }
                }}
              />
              Select all filtered
            </div>

            <div className="grid gap-2">
              {filtered.map((t) => (
                <button
                  key={t.id}
                  className="rounded-sm border border-line px-4 py-3 text-left transition hover:border-accent/50"
                  onClick={() => setSelectedId(t.id)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="checkbox"
                      checked={bulkSelectedIds.includes(t.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        setBulkSelectedIds((cur) =>
                          e.target.checked ? Array.from(new Set([...cur, t.id])) : cur.filter((id) => id !== t.id),
                        );
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className={statusBadge(t.status)}>{STATUS_LABEL[t.status]}</span>
                    <span className={severityBadge(t.priority)}>{SEVERITY_LABEL[t.priority]}</span>
                    <SlaBadge state={t.slaState} />
                    <span className="badge">{ageLabel(t.createdAt)} old</span>
                    {t.number !== undefined && <span className="badge">Case #{t.number}</span>}
                    <span className="font-semibold text-ink">{t.subject}</span>
                  </div>
                  <div className="mt-1 text-xs text-ink-dim">
                    {t.accountName} · {t.requesterEmail} · {t.assigneeEmail ? `assigned to ${t.assigneeEmail}` : "unassigned"} · updated{" "}
                    {new Date(t.updatedAt).toLocaleString()}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * Admin-tunable auto-assignment queues (§2). Each category (plus a "default" fallback)
 * maps to an ordered, comma/newline-separated list of staff emails; new tickets in that
 * category are round-robin assigned to the least-loaded staffer in the queue. Empty
 * queues disable auto-assignment for that category.
 */
function QueueSettingsPanel({ api }: { api: Api }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .getSupportSettings()
      .then((r) => {
        if (!alive) return;
        const next: Record<string, string> = {};
        for (const key of QUEUE_KEYS) next[key] = (r.settings.queues[key] ?? []).join(", ");
        setValues(next);
      })
      .catch(() => alive && setError("Could not load support settings."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [api]);

  function parseEmails(raw: string): string[] {
    return raw
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter((e) => e !== "");
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const queues: Record<string, string[]> = {};
      for (const key of QUEUE_KEYS) {
        const emails = parseEmails(values[key] ?? "");
        if (emails.length > 0) queues[key] = emails;
      }
      const r = await api.setSupportSettings({ queues });
      const next: Record<string, string> = {};
      for (const key of QUEUE_KEYS) next[key] = (r.settings.queues[key] ?? []).join(", ");
      setValues(next);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save support settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <SectionTitle
        kicker="Admin · Configure"
        title="Email & notifications"
        desc="One place to manage the emails the portal uses. Enter comma- or space-separated addresses; changes take effect immediately and are audited."
      />
      {error && (
        <div className="mb-4">
          <Banner tone="error">{error}</Banner>
        </div>
      )}
      {saved && (
        <div className="mb-4">
          <Banner tone="success">Settings saved.</Banner>
        </div>
      )}
      {loading ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : (
        <div className="grid gap-6">
          <div>
            <h4 className="mb-1 text-sm font-semibold text-ink">Case auto-assignment queues</h4>
            <p className="mb-2 text-xs text-ink-muted">
              Route new cases to staff by category. A ticket is round-robin assigned to the
              least-loaded staffer in the matched queue; the Default queue catches categories
              without their own list. Leave a queue blank to disable auto-assignment for it.
            </p>
            <div className="grid gap-4">
              {QUEUE_KEYS.map((key) => (
                <div key={key}>
                  <label className="label">{QUEUE_LABEL[key] ?? key}</label>
                  <input
                    className="input"
                    placeholder="chris@pumamesh.com, second@pumamesh.com"
                    value={values[key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <button className="btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function MetricsPanel({
  metrics,
  loading,
  error,
  from,
  to,
  onFromChange,
  onToChange,
  onReload,
}: {
  metrics: TicketMetrics | null;
  loading: boolean;
  error: string | null;
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onReload: () => void;
}) {
  const byStatus = metrics ? topEntries(metrics.byStatus, 20) : [];
  const byPriority = metrics ? topEntries(metrics.byPriority, 20) : [];
  const byDay = metrics ? ascendingEntries(metrics.volumeByDay) : [];
  const perAccount = metrics ? topEntries(metrics.perAccount, 10) : [];
  const perAgent = metrics ? topEntries(metrics.perAgent, 10) : [];

  return (
    <div>
      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">From</label>
            <input className="input !w-44" type="date" value={from} onChange={(e) => onFromChange(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input !w-44" type="date" value={to} onChange={(e) => onToChange(e.target.value)} />
          </div>
          <button className="btn-primary" disabled={loading} onClick={onReload}>
            {loading ? "Loading…" : "Load metrics"}
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-danger">{error}</p>}
      </Card>

      {metrics && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <Stat label="Total" value={metrics.total} />
            <Stat label="Backlog open" value={metrics.backlogOpen} />
            <Stat label="Avg first response (mins)" value={textOrDash(metrics.avgFirstResponseMins)} />
            <Stat label="Avg resolution (mins)" value={textOrDash(metrics.avgResolutionMins)} />
            <Stat label="CSAT avg" value={metrics.csatAvg == null ? "—" : metrics.csatAvg.toFixed(2)} />
            <Stat label="CSAT count" value={metrics.csatCount} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-ink">By status</h3>
              <MetricList entries={byStatus} />
            </Card>
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-ink">By priority</h3>
              <MetricList entries={byPriority} />
            </Card>
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-ink">Volume by day</h3>
              <MetricList entries={byDay} />
            </Card>
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-ink">Top accounts</h3>
              <MetricList entries={perAccount} />
            </Card>
            <Card>
              <h3 className="mb-3 text-sm font-semibold text-ink">Top agents</h3>
              <MetricList entries={perAgent} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function MetricList({ entries }: { entries: Array<[string, number]> }) {
  if (entries.length === 0) return <p className="text-sm text-ink-muted">No data.</p>;
  const max = Math.max(...entries.map(([, value]) => value), 1);
  return (
    <div className="space-y-2">
      {entries.map(([label, value]) => (
        <div key={label}>
          <div className="mb-1 flex items-center justify-between gap-2 text-xs text-ink-dim">
            <span className="truncate">{label}</span>
            <span>{value}</span>
          </div>
          <div className="h-2 rounded-sm bg-bg-soft">
            <div className="h-2 rounded-sm bg-accent" style={{ width: `${Math.max((value / max) * 100, 2)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SupportDetail({
  api,
  meEmail,
  ticket,
  onChange,
  onBack,
  onReload,
}: {
  api: Api;
  meEmail: string;
  ticket: Ticket;
  onChange: (t: Ticket) => void;
  onBack: () => void;
  onReload: () => void;
}) {
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState(ticket.assigneeEmail ?? "");
  const [tagsInput, setTagsInput] = useState((ticket.tags ?? []).join(", "));
  const [watchersInput, setWatchersInput] = useState((ticket.watchers ?? []).join(", "));
  const [buildPath, setBuildPath] = useState(ticket.linkedBuildId ?? "");
  const [mergeTarget, setMergeTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [canned, setCanned] = useState<CannedResponse[]>([]);
  const [cannedId, setCannedId] = useState("");
  const [cannedTitle, setCannedTitle] = useState("");
  const [cannedBody, setCannedBody] = useState("");

  const [builds, setBuilds] = useState<Array<{ line: string; candidate: BuildCandidate }>>([]);

  function firstNameFromEmail(email: string): string {
    const local = email.split("@")[0] ?? "";
    const first = local.split(/[._-]/)[0] ?? "";
    return first || local || "customer";
  }

  function applyTemplate(raw: string): string {
    return raw
      .replaceAll("{{account}}", ticket.accountName)
      .replaceAll("{{first_name}}", firstNameFromEmail(ticket.requesterEmail));
  }

  async function refreshCanned() {
    try {
      const r = await api.listCannedResponses();
      setCanned(r.canned);
    } catch {
      setError("Could not load canned responses.");
    }
  }

  async function refreshBuilds() {
    try {
      const r = await api.listBuildCandidates();
      const flat = Object.entries(r.lines).flatMap(([line, candidates]) =>
        candidates.map((candidate) => ({ line, candidate })),
      );
      setBuilds(flat);
    } catch {
      setError("Could not load build candidates.");
    }
  }

  useEffect(() => {
    void refreshCanned();
    void refreshBuilds();
  }, [ticket.id]);

  async function patch(patch: {
    status?: string;
    priority?: string;
    assigneeEmail?: string | null;
  }) {
    setBusy(true);
    setError(null);
    try {
      // Send the version we last read so a concurrent edit by another agent is rejected
      // (409) instead of silently overwritten. §2
      const { ticket: t } = await api.adminUpdateTicket(ticket.id, {
        ...patch,
        expectedUpdatedAt: ticket.updatedAt,
      });
      onChange(t);
      setAssignee(t.assigneeEmail ?? "");
      setTagsInput((t.tags ?? []).join(", "));
      setWatchersInput((t.watchers ?? []).join(", "));
      setBuildPath(t.linkedBuildId ?? "");
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 409
          ? "Someone else updated this case. Reload to see the latest changes, then retry."
          : "Could not update the case.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function sendReply() {
    if (reply.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      const { ticket: t } = await api.adminAddTicketComment(ticket.id, {
        body: reply,
        internal: false,
      });
      onChange(t);
      setReply("");
    } catch {
      setError("Could not send the reply.");
    } finally {
      setBusy(false);
    }
  }

  async function addNote() {
    if (note.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      const { ticket: t } = await api.adminAddTicketComment(ticket.id, {
        body: note,
        internal: true,
      });
      onChange(t);
      setNote("");
    } catch {
      setError("Could not add the internal note.");
    } finally {
      setBusy(false);
    }
  }

  async function saveTags() {
    setBusy(true);
    setError(null);
    try {
      const tags = tagsInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { ticket: t } = await api.adminSetTicketTags(ticket.id, tags);
      onChange(t);
    } catch {
      setError("Could not save tags.");
    } finally {
      setBusy(false);
    }
  }

  async function saveWatchers() {
    setBusy(true);
    setError(null);
    try {
      const watchers = watchersInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { ticket: t } = await api.adminSetTicketWatchers(ticket.id, watchers);
      onChange(t);
    } catch {
      setError("Could not save watchers.");
    } finally {
      setBusy(false);
    }
  }

  async function saveBuildLink() {
    setBusy(true);
    setError(null);
    try {
      const { ticket: t } = await api.adminLinkTicketBuild(
        ticket.id,
        buildPath.trim() === "" ? null : buildPath.trim(),
      );
      onChange(t);
    } catch {
      setError("Could not save linked build.");
    } finally {
      setBusy(false);
    }
  }

  async function mergeTicket() {
    if (mergeTarget.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      await api.adminMergeTicket(ticket.id, mergeTarget.trim());
      onBack();
      onReload();
    } catch {
      setError("Could not merge this ticket.");
    } finally {
      setBusy(false);
    }
  }

  async function saveCanned() {
    if (cannedTitle.trim() === "" || cannedBody.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      await api.upsertCannedResponse({ title: cannedTitle.trim(), body: cannedBody });
      setCannedTitle("");
      setCannedBody("");
      await refreshCanned();
    } catch {
      setError("Could not save canned response.");
    } finally {
      setBusy(false);
    }
  }

  async function removeCanned(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteCannedResponse(id);
      if (cannedId === id) setCannedId("");
      await refreshCanned();
    } catch {
      setError("Could not delete canned response.");
    } finally {
      setBusy(false);
    }
  }

  function insertCanned() {
    if (cannedId === "") return;
    const selected = canned.find((c) => c.id === cannedId);
    if (!selected) return;
    const hydrated = applyTemplate(selected.body);
    setReply((cur) => (cur.trim() === "" ? hydrated : `${cur}\n\n${hydrated}`));
  }

  async function download(objectPath: string) {
    try {
      const { url } = await api.adminTicketAttachmentUrl(ticket.id, objectPath);
      window.location.assign(url);
    } catch {
      setError("Could not open that attachment.");
    }
  }

  // Staff retry for attachments stuck "pending" after a transient scanner outage, so a lost
  // log becomes downloadable without waiting for the scheduled sweep. §4 F-5
  async function rescanAttachments() {
    setBusy(true);
    setError(null);
    try {
      await api.adminRescanTicketAttachments(ticket.id);
      const { ticket: t } = await api.adminGetTicket(ticket.id);
      onChange(t);
    } catch {
      setError("Could not retry the attachment scan.");
    } finally {
      setBusy(false);
    }
  }

  // Every attachment reference on the case — top-level plus one per comment. Drives the
  // staff "Retry scan" affordance when a transient scanner outage left any stuck pending. §4 F-5
  const allAttachments = [
    ...ticket.attachments,
    ...ticket.comments.flatMap((c) => c.attachments),
  ];
  const hasPendingScan = allAttachments.some((a) => a.scanStatus === "pending");

  return (
    <div>
      <button className="btn-ghost mb-3" onClick={onBack}>
        ← All cases
      </button>

      {error && (
        <div className="mb-4">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      <div className="mb-1 flex flex-wrap items-center gap-2 text-lg font-semibold text-ink">
        {ticket.number !== undefined && <span className="badge">Case #{ticket.number}</span>}
        <span>{ticket.subject}</span>
        <SlaBadge state={ticket.slaState} />
        <span className="badge">{ageLabel(ticket.createdAt)} old</span>
      </div>
      <div className="mb-4 text-xs text-ink-dim">
        {ticket.accountName} · {ticket.requesterEmail} · {ticket.category} · opened{" "}
        {new Date(ticket.createdAt).toLocaleString()}
      </div>

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="label">Status</label>
            <select
              className="input !w-44"
              value={ticket.status}
              disabled={busy}
              onChange={(e) => void patch({ status: e.target.value })}
            >
              {TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Severity</label>
            <select
              className="input !w-44"
              value={ticket.priority}
              disabled={busy}
              onChange={(e) => void patch({ priority: e.target.value })}
            >
              {TICKET_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {SEVERITY_LABEL[p]}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[14rem] flex-1">
            <label className="label">Assignee</label>
            <div className="flex gap-2">
              <input
                className="input"
                placeholder="staff@pumamesh.com"
                value={assignee}
                disabled={busy}
                onChange={(e) => setAssignee(e.target.value)}
              />
              <button
                className="btn-ghost shrink-0"
                disabled={busy}
                onClick={() => {
                  setAssignee(meEmail);
                  void patch({ assigneeEmail: meEmail });
                }}
              >
                Me
              </button>
              <button
                className="btn-primary shrink-0"
                disabled={busy}
                onClick={() => void patch({ assigneeEmail: assignee.trim() === "" ? null : assignee.trim() })}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <label className="label">Tags</label>
            <div className="flex gap-2">
              <input
                className="input"
                value={tagsInput}
                disabled={busy}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="infra, rl10, incident"
              />
              <button className="btn-primary" disabled={busy} onClick={() => void saveTags()}>
                Save
              </button>
            </div>
          </div>
          <div>
            <label className="label">Watchers</label>
            <div className="flex gap-2">
              <input
                className="input"
                value={watchersInput}
                disabled={busy}
                onChange={(e) => setWatchersInput(e.target.value)}
                placeholder="ops@pumamesh.com, oncall@pumamesh.com"
              />
              <button className="btn-primary" disabled={busy} onClick={() => void saveWatchers()}>
                Save
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <label className="label">Linked build</label>
            <div className="flex gap-2">
              <select
                className="input"
                value={buildPath}
                disabled={busy}
                onChange={(e) => setBuildPath(e.target.value)}
              >
                <option value="">No linked build</option>
                {builds.map(({ line, candidate }) => {
                  const name = candidate.artifact?.version ?? candidate.objectPath;
                  return (
                    <option key={candidate.objectPath} value={candidate.objectPath}>
                      {line} · {name}
                    </option>
                  );
                })}
              </select>
              <button className="btn-primary" disabled={busy} onClick={() => void saveBuildLink()}>
                Save
              </button>
            </div>
            <div className="mt-1 text-xs text-ink-dim">
              Current: {ticket.linkedBuildId ? ticket.linkedBuildId : "none"}
            </div>
          </div>
          <div>
            <label className="label">Merge into ticket</label>
            <div className="flex gap-2">
              <input
                className="input"
                value={mergeTarget}
                disabled={busy}
                onChange={(e) => setMergeTarget(e.target.value)}
                placeholder="TCK-12345"
              />
              <button className="btn-ghost" disabled={busy || mergeTarget.trim() === ""} onClick={() => void mergeTicket()}>
                Merge
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="mb-4 rounded-sm border border-line p-4">
        <MarkdownBody rendered={ticket.renderedDescription} raw={ticket.description} />
      </div>

      <div className="space-y-2">
        {ticket.comments.map((c) => (
          <div
            key={c.id}
            className={`rounded-sm border p-3 ${
              c.internal
                ? "border-danger/40 bg-danger/5"
                : c.authorType === "staff"
                  ? "border-accent/40"
                  : "border-line"
            }`}
          >
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-ink-dim">
              <span className={c.authorType === "customer" ? "badge" : "badge-accent"}>
                {c.authorType === "ai"
                  ? "AI Triage"
                  : c.authorType === "staff"
                    ? "PumaMesh"
                    : "Customer"}
              </span>
              {c.internal && <span className="badge">internal</span>}
              <span>{c.authorEmail}</span>
              <span>· {new Date(c.createdAt).toLocaleString()}</span>
              {c.mentions && c.mentions.length > 0 && (
                <span className="text-ink-dim">· @ {c.mentions.join(", ")}</span>
              )}
            </div>
            <MarkdownBody rendered={c.renderedBody} raw={c.body} />
            {c.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {c.attachments.map((a) =>
                  a.scanStatus === "pending" ? (
                    <span key={a.objectPath} className="badge text-ink-dim">
                      ⏳ {a.filename} (scan pending)
                    </span>
                  ) : a.scanStatus === "infected" ? (
                    <span key={a.objectPath} className="badge text-danger">
                      ⚠ {a.filename} (blocked)
                    </span>
                  ) : (
                    <button
                      key={a.objectPath}
                      className="badge hover:text-accent-strong"
                      onClick={() => void download(a.objectPath)}
                    >
                      ↓ {a.filename}
                    </button>
                  ),
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {hasPendingScan && (
        <div className="mt-4 flex items-center gap-3 rounded-sm border border-line p-3 text-sm">
          <span className="text-ink-dim">
            An attachment scan is pending (a transient scanner outage blocks the download).
          </span>
          <button
            className="btn-ghost !py-0.5 text-xs"
            disabled={busy}
            onClick={() => void rescanAttachments()}
            title="Re-run the malware scan on attachments stuck in a pending state"
          >
            Retry scan
          </button>
        </div>
      )}

      {ticket.attachments.length > 0 && (
        <div className="mt-4">
          <div className="label">All attachments</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {ticket.attachments.map((a) =>
              a.scanStatus === "pending" ? (
                <span key={a.objectPath} className="badge text-ink-dim">
                  ⏳ {a.filename} ({formatBytes(a.sizeBytes)}) — scan pending
                </span>
              ) : a.scanStatus === "infected" ? (
                <span key={a.objectPath} className="badge text-danger">
                  ⚠ {a.filename} ({formatBytes(a.sizeBytes)}) — blocked
                </span>
              ) : (
                <button
                  key={a.objectPath}
                  className="badge hover:text-accent-strong"
                  onClick={() => void download(a.objectPath)}
                >
                  ↓ {a.filename} ({formatBytes(a.sizeBytes)})
                </button>
              ),
            )}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <label className="label">Reply to customer</label>
          <div className="mb-2 flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <label className="label">Canned response</label>
              <select className="input" value={cannedId} onChange={(e) => setCannedId(e.target.value)}>
                <option value="">Select template…</option>
                {canned.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn-ghost" disabled={cannedId === ""} onClick={insertCanned}>
              Insert
            </button>
          </div>
          <p className="mb-2 text-xs text-ink-dim">
            Visible to everyone on the customer's account. They'll get an email letting them know to sign in.
          </p>
          <MentionTextarea
            api={api}
            className="input min-h-[96px]"
            value={reply}
            disabled={busy}
            onChange={setReply}
            ticketId={ticket.id}
            placeholder="Write a customer-facing reply. Type @name to notify a teammate, the customer, or PumaMesh."
          />
          <div className="mt-2 flex justify-end">
            <button className="btn-primary" disabled={busy || reply.trim() === ""} onClick={() => void sendReply()}>
              {busy ? "Sending…" : "Send reply"}
            </button>
          </div>
        </Card>

        <Card className="border-danger/40">
          <label className="label">Internal note</label>
          <p className="mb-2 text-xs text-ink-dim">
            PumaMesh staff only — never shown to the customer.
          </p>
          <MentionTextarea
            api={api}
            className="input min-h-[96px]"
            value={note}
            disabled={busy}
            onChange={setNote}
            ticketId={ticket.id}
            placeholder="Internal triage note. Type @name to notify a PumaMesh teammate or the customer."
          />
          <div className="mt-2 flex justify-end">
            <button className="btn-ghost" disabled={busy || note.trim() === ""} onClick={() => void addNote()}>
              {busy ? "Saving…" : "Add internal note"}
            </button>
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <h3 className="mb-2 text-sm font-semibold text-ink">Manage canned responses</h3>
        <div className="grid gap-3 lg:grid-cols-[1fr_2fr_auto]">
          <input
            className="input"
            value={cannedTitle}
            disabled={busy}
            onChange={(e) => setCannedTitle(e.target.value)}
            placeholder="Template title"
          />
          <input
            className="input"
            value={cannedBody}
            disabled={busy}
            onChange={(e) => setCannedBody(e.target.value)}
            placeholder="Template body (supports {{account}} and {{first_name}})"
          />
          <button className="btn-primary" disabled={busy} onClick={() => void saveCanned()}>
            Save template
          </button>
        </div>
        {canned.length > 0 && (
          <div className="mt-3 grid gap-2">
            {canned.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 rounded-sm border border-line px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">{c.title}</div>
                  <div className="truncate text-xs text-ink-dim">{c.body}</div>
                </div>
                <button className="btn-ghost" disabled={busy} onClick={() => void removeCanned(c.id)}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
